import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Tooltip,
  Checkbox,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BoundedNumberField from "components/BoundedNumberField";
import { Delete } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const EmployeeAccordion = ({
  emp,
  expanded,
  handleExpand,
  handleChange,
  isScenario,
  handleDeleteEmployee,
  isSorted,
  placement,
}) => {
  const { t } = useTranslation();
  const [tempSalary, setTempSalary] = useState(emp.salary?.toString() || "");

  const handleBlur = (id, field, value) => {
    const oldValue = emp[field];
    if (String(value) !== String(oldValue)) {
      handleChange(id, field, value);
    }
    // handleChange(id, field, value);
  };
  return (
    <Accordion
      key={emp.employeeId}
      expanded={expanded === emp.employeeId}
      onChange={() => handleExpand(emp.employeeId)}
      sx={{
        mb: 1,
        boxShadow: "none",
        borderRadius: 1,
        backgroundColor: isSorted
          ? emp.score < 60
            ? "rgba(255, 0, 0, 0.05)" // light red
            : emp.score > 90
            ? "rgba(0, 200, 0, 0.08)" // light green
            : "#fff"
          : "#fff",
        border: "1px solid #ddd",
        transition: "background-color 0.3s ease",
      }}
    >
      <AccordionSummary
        expandIcon={!isSorted && <ExpandMoreIcon />}
        sx={{
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {/* 👇 Placement Number (if sorted) */}
          {isSorted && (
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                bgcolor: "primary.main",
                color: "white",
                fontSize: "0.75rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
              }}
            >
              {placement}
            </Box>
          )}

          {/* 👇 Employee Info */}
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {emp.employeeName}
            </Typography>
            {isSorted ? (
              <Typography variant="caption" color="text.secondary">
                {t("user.employeeDrawer.score")}: {emp.score ?? "-"} |{" "}
                {t("user.employeeDrawer.salary")}: ${emp.salary ? emp.salary.toLocaleString() : 0}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {t("user.employeeDrawer.pir")}: {emp.pir ?? "-"} | {t("user.employeeDrawer.salary")}
                : ${emp.salary ? emp.salary.toLocaleString() : 0}
              </Typography>
            )}
          </Box>
        </Box>

        {isScenario && !isSorted && (
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteEmployee(emp.employeeId);
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        )}
      </AccordionSummary>

      {!isSorted && (
        <AccordionDetails
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            backgroundColor: "#fafafa",
            border: "1px solid #eee",
            borderRadius: 1,
          }}
        >
          <TextField
            size="small"
            label={t("user.employeeDrawer.dateOfBirth")}
            type="date"
            InputLabelProps={{ shrink: true }}
            defaultValue={emp.dateOfBirth || ""}
            onBlur={(e) => handleBlur(emp.employeeId, "dateOfBirth", e.target.value)}
          />
          {/* Salary */}
          <TextField
            size="small"
            fullWidth
            label={t("user.employeeDrawer.salary")}
            type="text" // change from "number" to "text" to allow formatted display
            value={tempSalary === "" ? "" : Number(tempSalary).toLocaleString()}
            onChange={(e) => {
              const rawValue = e.target.value.replace(/,/g, "");
              if (rawValue === "" || (/^\d+$/.test(rawValue) && Number(rawValue) >= 0)) {
                setTempSalary(rawValue);
              }
            }}
            onBlur={() => handleBlur(emp.employeeId, "salary", Number(tempSalary))}
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              min: 0,
            }}
          />
          {/* PIR with Tooltip */}
          {/* <Tooltip title={t("user.employeeDrawer.positionInRange")}>
            <TextField
              size="small"
              label={t("user.employeeDrawer.pir")}
              type="number"
              defaultValue={emp.pir || ""}
              onBlur={(e) => handleBlur(emp.employeeId, "pir", e)}
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Tooltip> */}

          <BoundedNumberField
            id={emp.employeeId}
            label={t("user.employeeDrawer.pir")}
            field="pir"
            value={emp.pir}
            min={0}
            onSave={(id, payload) => handleBlur(id, "pir", payload["pir"])}
          />

          {/* Experience (0–100) */}
          <BoundedNumberField
            id={emp.employeeId}
            label={t("user.employeeDrawer.experienceYears")}
            field="experienceYears"
            value={emp.experienceYears}
            min={0}
            max={100}
            onSave={(id, payload) => handleBlur(id, "experienceYears", payload["experienceYears"])}
          />
          {/* Performance Score (0–200) */}
          <BoundedNumberField
            id={emp.employeeId}
            label={t("user.employeeDrawer.performanceScore")}
            field="performanceScore"
            value={emp.performanceScore}
            min={0}
            max={200}
            onSave={(id, payload) =>
              handleBlur(id, "performanceScore", payload["performanceScore"])
            }
          />

          {/* Competency Score (0–200) */}
          <BoundedNumberField
            id={emp.employeeId}
            label={t("user.employeeDrawer.competencyScore")}
            field="competencyScore"
            value={emp.competencyScore}
            min={0}
            max={200}
            onSave={(id, payload) => handleBlur(id, "competencyScore", payload["competencyScore"])}
          />

          {/* Credentials Score (0–200) */}
          <BoundedNumberField
            id={emp.employeeId}
            label={t("user.employeeDrawer.credentialsScore")}
            field="credentialsScore"
            value={emp.credentialsScore}
            min={0}
            max={200}
            onSave={(id, payload) =>
              handleBlur(id, "credentialsScore", payload["credentialsScore"])
            }
          />

          {/* Privilege Level (1–10) */}
          <BoundedNumberField
            id={emp.employeeId}
            label={t("user.employeeDrawer.privilegeLevel")}
            field="privilegesLevel"
            value={emp.privilegesLevel}
            min={0}
            max={10}
            onSave={(id, payload) => handleBlur(id, "privilegesLevel", payload["privilegesLevel"])}
          />

          {/* Talented Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={emp.talentFlag || false}
                onChange={(e) => handleBlur(emp.employeeId, "talentFlag", e.target.checked)}
              />
            }
            label={t("user.employeeDrawer.talented")}
          />
        </AccordionDetails>
      )}
    </Accordion>
  );
};

EmployeeAccordion.propTypes = {
  emp: PropTypes.object.isRequired,
  expanded: PropTypes.number,
  handleExpand: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  isScenario: PropTypes.bool.isRequired,
  handleDeleteEmployee: PropTypes.func.isRequired,
  isSorted: PropTypes.bool.isRequired,
  placement: PropTypes.number.isRequired,
};

export default EmployeeAccordion;
